import React from "react"
import type { ApolloError } from "@apollo/client"
import { Button, componentStyles } from "@mallardbay/lib-react-components"

import ErrorBanner from "~components/shared/error-banner"
import { useCheckoutFlow } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-provider"
import COPY from "~config/copy-constants"
import Box from "~components/shared/todo-lib-react-components/box"
import { useIsMobile } from "~components/shared/todo-lib-react-components/hooks/use-is-mobile"
import ContactSupportBanner from "~components/contact-support-banner"
import { BUTTON_TYPES } from "~config/constants"

interface Props {
    readonly isContinueDisabled?: boolean
    readonly onContinueClickOverride?: () => void
    readonly isContinueButtonFormSubmit?: boolean
    readonly isLoading?: boolean
    readonly error?: ApolloError | Error | null
    readonly continueLabel?: string
}

export default function CheckoutFlowNavButtons({
    isContinueDisabled,
    onContinueClickOverride,
    isContinueButtonFormSubmit,
    isLoading,
    error,
    continueLabel = COPY.CONTINUE,
}: Props) {
    const isMobile = useIsMobile()
    const styles = useStyles({ isMobile })

    const { goToNextStep } = useCheckoutFlow()

    const onClick = getClickHandler({
        onContinueClickOverride,
        goToNextStep,
        isContinueButtonFormSubmit,
    })
    const buttonType = isContinueButtonFormSubmit
        ? BUTTON_TYPES.SUBMIT
        : BUTTON_TYPES.BUTTON

    return (
        <>
            <Box style={styles.errorContainer}>
                <ErrorBanner error={error} />
            </Box>
            <Box style={styles.root}>
                <Button
                    gradientEnabled
                    onClick={onClick}
                    isDisabled={isContinueDisabled}
                    isLoading={isLoading}
                    style={styles.continueButton}
                    type={buttonType}
                >
                    {continueLabel}
                </Button>
                {!isMobile && (
                    <Box style={styles.contactButtonContainer}>
                        <ContactSupportBanner />
                    </Box>
                )}
            </Box>
        </>
    )
}

function useStyles({ isMobile }: { readonly isMobile: boolean }) {
    return componentStyles({
        root: {
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
        },
        contactButtonContainer: {
            display: { sm: "none", lg: "block" },
        },
        continueButton: {
            marginTop: "1rem",
        },
        errorContainer: {
            ...(isMobile && {
                position: "absolute",
                bottom: 150,
                left: 100,
            }),
        },
    })
}

function getClickHandler({
    onContinueClickOverride,
    goToNextStep,
    isContinueButtonFormSubmit,
}: {
    onContinueClickOverride?: () => void
    goToNextStep: () => void
    isContinueButtonFormSubmit?: boolean
}) {
    // If the continue button is a form submit, we don't want to pass
    // any click handler. The form will be responsible for handling navigation and such.
    // The button being a "submit" type will handle submitting the parent form.
    if (isContinueButtonFormSubmit) return undefined

    const onClick = onContinueClickOverride ?? goToNextStep

    // Wrapping the click handler in a function to prevent it from being called with click events.
    return () => onClick()
}
